import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { CheckCircle, AlertTriangle, Loader2, X, Plus, Trash2 } from 'lucide-react';
import PinEntryDialog from './PinEntryDialog';

interface CorrectiveAction {
  actionTitle: string;
  coachingGiven: string;
  actionsRequired: string;
  reassessmentDate?: string;
}

type CompletionResult = { success: boolean; message: string; pinAttemptsRemaining?: number; lockedUntil?: string };

interface AssessmentOutcomeSheetProps {
  anyNotYet: boolean;
  moduleName: string;
  traineeName: string;
  submitting: boolean;
  isPreview?: boolean;
  onClose: () => void;
  onSubmit: (outcome: 'Approved' | 'Further Training', notes: string, actions: CorrectiveAction[], pin: string) => Promise<CompletionResult>;
}

export default function AssessmentOutcomeSheet({ anyNotYet, moduleName, traineeName, submitting, isPreview, onClose, onSubmit }: AssessmentOutcomeSheetProps) {
  const defaultOutcome: 'Approved' | 'Further Training' = anyNotYet ? 'Further Training' : 'Approved';
  const [outcome, setOutcome] = useState<'Approved' | 'Further Training'>(defaultOutcome);
  const [notes, setNotes] = useState('');
  const [actions, setActions] = useState<CorrectiveAction[]>(
    anyNotYet ? [{ actionTitle: '', coachingGiven: '', actionsRequired: '', reassessmentDate: '' }] : []
  );
  const [showPin, setShowPin] = useState(false);

  const addAction = () => setActions([...actions, { actionTitle: '', coachingGiven: '', actionsRequired: '', reassessmentDate: '' }]);
  const removeAction = (i: number) => setActions(actions.filter((_, idx) => idx !== i));
  const updateAction = (i: number, field: keyof CorrectiveAction, value: string) => {
    const updated = [...actions];
    updated[i] = { ...updated[i], [field]: value };
    setActions(updated);
  };

  const canSubmit = outcome === 'Approved' || (notes.trim() || actions.some(a => a.actionTitle.trim()));

  const handleConfirmClick = () => {
    if (isPreview) {
      // Preview mode — simulate locally, no real endpoint call, no PIN
      onSubmit(outcome, notes, outcome === 'Further Training' ? actions.filter(a => a.actionTitle.trim()) : [], '0000');
      return;
    }
    setShowPin(true);
  };

  return (
    <>
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-end justify-center">
        <div className="w-full max-w-lg bg-card border-t border-border rounded-t-2xl max-h-[85vh] overflow-y-auto safe-area-pb">
          <div className="sticky top-0 bg-card px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-foreground">Complete Assessment</h2>
              {isPreview && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-amber-500/20 text-amber-600">PREVIEW</span>}
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center touch-manipulation">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-4 py-4 space-y-4">
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{traineeName}</span> — {moduleName}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setOutcome('Approved')}
                disabled={anyNotYet}
                className={`flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 transition-all touch-manipulation ${
                  outcome === 'Approved' ? 'border-primary bg-primary/10' : 'border-border bg-card'
                } ${anyNotYet ? 'opacity-40 pointer-events-none' : ''}`}
              >
                <CheckCircle className={`w-6 h-6 ${outcome === 'Approved' ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className={`text-sm font-semibold ${outcome === 'Approved' ? 'text-primary' : 'text-muted-foreground'}`}>Approve</span>
              </button>
              <button
                onClick={() => setOutcome('Further Training')}
                className={`flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 transition-all touch-manipulation ${
                  outcome === 'Further Training' ? 'border-destructive bg-destructive/10' : 'border-border bg-card'
                }`}
              >
                <AlertTriangle className={`w-6 h-6 ${outcome === 'Further Training' ? 'text-destructive' : 'text-muted-foreground'}`} />
                <span className={`text-sm font-semibold ${outcome === 'Further Training' ? 'text-destructive' : 'text-muted-foreground'}`}>Further Training</span>
              </button>
            </div>

            {anyNotYet && outcome === 'Further Training' && (
              <p className="text-xs text-destructive/80 italic">One or more criteria scored &quot;Not Yet&quot; — approval is not available.</p>
            )}

            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
                {outcome === 'Approved' ? 'Notes (optional)' : 'Corrective Action Notes *'}
              </label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder={outcome === 'Approved' ? 'Any observations or feedback...' : 'Describe what needs improvement...'}
                className="text-sm h-20 resize-none"
              />
            </div>

            {outcome === 'Further Training' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Corrective Actions</label>
                  <button onClick={addAction} className="text-xs text-primary font-medium flex items-center gap-0.5 touch-manipulation">
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>
                {actions.map((a, i) => (
                  <div key={i} className="p-3 bg-muted/30 border border-border rounded-xl mb-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-muted-foreground uppercase">Action {i + 1}</span>
                      {actions.length > 1 && (
                        <button onClick={() => removeAction(i)} className="text-destructive/60 hover:text-destructive touch-manipulation">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <Input value={a.actionTitle} onChange={e => updateAction(i, 'actionTitle', e.target.value)} placeholder="Action title" className="text-sm h-9" />
                    <Textarea value={a.coachingGiven} onChange={e => updateAction(i, 'coachingGiven', e.target.value)} placeholder="Coaching given..." className="text-sm h-14 resize-none" />
                    <Input value={a.actionsRequired} onChange={e => updateAction(i, 'actionsRequired', e.target.value)} placeholder="Actions required" className="text-sm h-9" />
                    <Input type="date" value={a.reassessmentDate || ''} onChange={e => updateAction(i, 'reassessmentDate', e.target.value)} className="text-sm h-9" />
                  </div>
                ))}
              </div>
            )}

            <Button
              onClick={handleConfirmClick}
              disabled={submitting || !canSubmit}
              className={`w-full h-12 text-sm font-semibold touch-manipulation ${
                outcome === 'Further Training' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''
              }`}
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Submitting...</> :
               isPreview ? (outcome === 'Approved' ? 'Preview: Approve' : 'Preview: Submit Further Training') :
               outcome === 'Approved' ? 'Enter PIN to Approve' : 'Enter PIN to Submit'}
            </Button>
          </div>
        </div>
      </div>

      <PinEntryDialog
        open={showPin}
        onClose={() => setShowPin(false)}
        onSubmit={async (pin) => {
          const filteredActions = outcome === 'Further Training' ? actions.filter(a => a.actionTitle.trim()) : [];
          const result = await onSubmit(outcome, notes, filteredActions, pin);
          if (result.success) {
            setShowPin(false);
          }
          return result;
        }}
      />
    </>
  );
}
